/**
 * CarteraFunnelSection
 * Funnel agregado de la cartera completa: suma de todos los clientes activos.
 * Reusa la lógica visual del TrayectoriaSection pero con totales.
 */
import { useMemo, useState } from 'react';
import { Lightbulb } from 'lucide-react';
import { useBackofficeOffers } from '@/features/credit/hooks/useBackofficeOffers';
import { useBackofficeApplications } from '@/features/credit/hooks/useBackofficeApplications';
import { useBackofficeCredits } from '@/features/credit/hooks';
import { useScoring } from '@/features/scoring/hooks/useScoring';
import { formatCurrency } from '@/utils/formatters';
import {
  creditScore,
  scoreColor,
  statusBadge,
  computeOverdueAmount,
  computeHealthGlobal,
  LegendDot,
} from '@/features/credit/utils/creditHealth';
import type { BackofficeCreditsListItem } from '@/types/backoffice-crud.types';

const ROWS_PER_PAGE = 25;

export function CarteraFunnelSection() {
  const { offers, isLoading: offersLoading } = useBackofficeOffers();
  const { applications, isLoading: appsLoading } = useBackofficeApplications();
  const { credits, isLoading: creditsLoading } = useBackofficeCredits();
  const { summary, isSummaryLoading } = useScoring();
  const [page, setPage] = useState(0);
  const [filter, setFilter] = useState<'all' | 'live' | 'problem' | 'paid'>('live');

  const isLoading = offersLoading || appsLoading || creditsLoading || isSummaryLoading;

  // ── Funnel sums ───────────────────────────────────────────────────────
  const totalOffers = offers.reduce((s, o) => s + (o.max_amount_mxn ?? 0), 0);
  const signedApps = applications.filter((a) => {
    const s = a.status?.toUpperCase();
    return s === 'SIGNED' || s === 'DISBURSED';
  });
  const totalApps = signedApps.reduce((s, a) => s + (a.amount_mxn ?? 0), 0);
  const totalDisbursed = credits.reduce((s, c) => s + (c.amount_mxn ?? 0), 0);
  const totalCobrado = credits.reduce(
    (s, c) => s + (c.weeks_paid ?? 0) * (c.weekly_payment_mxn ?? 0),
    0,
  );
  const totalOverdue = credits.reduce((s, c) => s + computeOverdueAmount(c), 0);
  const totalFuture = credits.reduce((s, c) => {
    const remaining = (c.weeks_total ?? c.term_weeks ?? 0) - (c.weeks_paid ?? 0);
    const remainingAmount = Math.max(0, remaining * (c.weekly_payment_mxn ?? 0));
    return s + Math.max(0, remainingAmount - computeOverdueAmount(c));
  }, 0);

  const offerToAppDrop = totalOffers - totalApps;
  const appToCreditDrop = totalApps - totalDisbursed;

  const totalCapacity = summary?.pipeline?.total_potential_mxn ?? 0;
  const eligibleCount = summary?.pipeline?.eligible_count ?? 0;

  const healthGlobal = computeHealthGlobal(credits);

  // ── Counts por estado ─────────────────────────────────────────────────
  const counts = useMemo(() => {
    const c = { active: 0, delinquent: 0, defaulted: 0, paidOff: 0, total: credits.length };
    for (const credit of credits) {
      const s = credit.status?.toUpperCase();
      if (s === 'ACTIVE') c.active++;
      else if (s === 'DELINQUENT') c.delinquent++;
      else if (s === 'DEFAULTED' || s === 'CANCELLED') c.defaulted++;
      else if (s === 'PAID_OFF' || s === 'SETTLED') c.paidOff++;
    }
    return c;
  }, [credits]);

  // ── Lista filtrada y ordenada ─────────────────────────────────────────
  const filteredCredits = useMemo(() => {
    const list = credits.filter((c) => {
      const s = c.status?.toUpperCase();
      if (filter === 'all') return true;
      if (filter === 'live') return s === 'ACTIVE' || s === 'DELINQUENT';
      if (filter === 'problem') return s === 'DELINQUENT' || s === 'DEFAULTED' || (s === 'ACTIVE' && computeOverdueAmount(c) > 0);
      if (filter === 'paid') return s === 'PAID_OFF' || s === 'SETTLED';
      return true;
    });
    return [...list].sort((a, b) => creditScore(a) - creditScore(b));
  }, [credits, filter]);

  const totalPages = Math.max(1, Math.ceil(filteredCredits.length / ROWS_PER_PAGE));
  const pageCredits = filteredCredits.slice(page * ROWS_PER_PAGE, (page + 1) * ROWS_PER_PAGE);

  // ── Insights agregados ────────────────────────────────────────────────
  const insights = useMemo(
    () => buildCarteraInsights({ offers, signedApps, credits, counts, totalOverdue, healthGlobal }),
    [offers, signedApps, credits, counts, totalOverdue, healthGlobal],
  );

  if (isLoading) {
    return (
      <div className="rounded-md border border-border bg-card p-6 text-center text-sm text-muted-foreground">
        Cargando funnel agregado de cartera…
      </div>
    );
  }

  if (credits.length === 0 && offers.length === 0) {
    return (
      <div className="rounded-md border border-border bg-card p-6 text-center text-sm text-muted-foreground">
        Sin actividad crediticia en la cartera.
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {/* Hero row */}
      <div className="grid gap-3 lg:grid-cols-[1fr_1.2fr]">
        <CapacityHero capacity={totalCapacity} eligibleCount={eligibleCount} />
        <HealthHero score={healthGlobal} count={credits.length} counts={counts} />
      </div>

      {/* Detail row */}
      <div className="grid gap-3 lg:grid-cols-[1fr_1.2fr] lg:items-start">
        <FunnelCard
          offerCount={offers.length}
          totalOffers={totalOffers}
          appCount={signedApps.length}
          totalApps={totalApps}
          creditCount={credits.length}
          totalDisbursed={totalDisbursed}
          totalCobrado={totalCobrado}
          totalFuture={totalFuture}
          totalOverdue={totalOverdue}
          offerToAppDrop={offerToAppDrop}
          appToCreditDrop={appToCreditDrop}
        />

        <div className="rounded-md border border-border bg-card p-3">
          <div className="mb-2 flex items-center justify-between">
            <p className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Créditos por cliente
            </p>
            <FilterPills filter={filter} onFilterChange={(f) => { setFilter(f); setPage(0); }} counts={counts} />
          </div>

          {filteredCredits.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              No hay créditos en este filtro.
            </p>
          ) : (
            <ul className="flex flex-col">
              {pageCredits.map((credit) => (
                <CreditRow key={credit.credit_id} credit={credit} />
              ))}
            </ul>
          )}

          {totalPages > 1 && (
            <div className="mt-2 flex items-center justify-between border-t border-border pt-2 text-[11px] text-muted-foreground">
              <span>
                Mostrando {page * ROWS_PER_PAGE + 1}-{Math.min((page + 1) * ROWS_PER_PAGE, filteredCredits.length)} de {filteredCredits.length}
              </span>
              <div className="flex items-center gap-1.5">
                <button
                  type="button"
                  disabled={page <= 0}
                  onClick={() => setPage(page - 1)}
                  className="rounded border border-border bg-card px-2 py-0.5 text-foreground disabled:opacity-30"
                >
                  ‹
                </button>
                <span>
                  Pag. {page + 1} de {totalPages}
                </span>
                <button
                  type="button"
                  disabled={page >= totalPages - 1}
                  onClick={() => setPage(page + 1)}
                  className="rounded border border-border bg-card px-2 py-0.5 text-foreground disabled:opacity-30"
                >
                  ›
                </button>
              </div>
            </div>
          )}

          <div className="mt-3 flex flex-wrap gap-2 border-t border-border pt-2 text-[10px] text-muted-foreground">
            <LegendDot color="bg-green-500" label="90-100 puntual" />
            <LegendDot color="bg-lime-500" label="70-89 leve" />
            <LegendDot color="bg-yellow-500" label="40-69 frecuente" />
            <LegendDot color="bg-orange-500" label="20-39 grave" />
            <LegendDot color="bg-red-500" label="0-19 default" />
          </div>
        </div>
      </div>

      {/* Insights */}
      {insights.length > 0 && (
        <div className="rounded-r-md border border-border border-l-4 border-l-amber-500 bg-card p-4">
          <div className="mb-2 flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-wide text-amber-500">
            <Lightbulb size={14} /> Observaciones de cartera
          </div>
          <ul className="space-y-1.5">
            {insights.map((insight, idx) => (
              <li key={idx} className="flex gap-2 text-sm leading-relaxed text-foreground">
                <span className="text-amber-500">•</span>
                <span dangerouslySetInnerHTML={{ __html: insight }} />
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Hero cards ────────────────────────────────────────────────────────────

function CapacityHero({ capacity, eligibleCount }: { capacity: number; eligibleCount: number }) {
  return (
    <div className="flex items-center justify-between rounded-md border border-green-500/30 bg-gradient-to-br from-green-500/15 to-green-500/5 px-4 py-3">
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Capacidad de cartera
        </p>
        <p className="mt-0.5 text-[10px] text-muted-foreground">
          {eligibleCount} {eligibleCount === 1 ? 'cliente elegible' : 'clientes elegibles'}
        </p>
      </div>
      <div className="text-right">
        <p className="text-3xl font-extrabold tabular-nums text-green-500">
          {formatCurrency(capacity, 'MXN', 'es-MX')}
        </p>
      </div>
    </div>
  );
}

function HealthHero({
  score,
  count,
  counts,
}: {
  score: number;
  count: number;
  counts: { active: number; delinquent: number; defaulted: number; paidOff: number };
}) {
  const color = scoreColor(score);
  const cls =
    color.text === 'text-green-500'
      ? 'border-green-500/30 bg-gradient-to-br from-green-500/15 to-green-500/5'
      : color.text === 'text-lime-500'
      ? 'border-lime-500/30 bg-gradient-to-br from-lime-500/15 to-lime-500/5'
      : color.text === 'text-yellow-500'
      ? 'border-yellow-500/30 bg-gradient-to-br from-yellow-500/15 to-yellow-500/5'
      : color.text === 'text-orange-500'
      ? 'border-orange-500/30 bg-gradient-to-br from-orange-500/15 to-orange-500/5'
      : 'border-red-500/30 bg-gradient-to-br from-red-500/15 to-red-500/5';

  return (
    <div className={`flex items-center justify-between rounded-md border px-4 py-3 ${cls}`}>
      <div>
        <p className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          Salud crediticia de cartera
        </p>
        <p className="mt-0.5 text-[10px] text-muted-foreground">
          {count > 0 ? `promedio ponderado · ${count} ${count === 1 ? 'crédito' : 'créditos'}` : 'sin créditos'}
          {(counts.delinquent > 0 || counts.defaulted > 0) && (
            <span className="ml-2">
              · <span className="text-red-400">{counts.delinquent + counts.defaulted}</span> con problemas
            </span>
          )}
        </p>
      </div>
      <div className="text-right">
        <p className={`text-3xl font-extrabold tabular-nums ${color.text}`}>
          {count > 0 ? Math.round(score) : '—'}
        </p>
        <p className="text-[10px] text-muted-foreground">de 100</p>
      </div>
    </div>
  );
}

// ── Funnel ────────────────────────────────────────────────────────────────

function FunnelCard(props: {
  offerCount: number;
  totalOffers: number;
  appCount: number;
  totalApps: number;
  creditCount: number;
  totalDisbursed: number;
  totalCobrado: number;
  totalFuture: number;
  totalOverdue: number;
  offerToAppDrop: number;
  appToCreditDrop: number;
}) {
  const { totalOffers, totalApps, totalDisbursed, totalCobrado, totalFuture, totalOverdue } = props;
  const totalToShow = Math.max(totalOffers, 1);

  const pct = (v: number) => ((v / totalToShow) * 100).toFixed(0);
  const relPct = (v: number, base: number) => (base > 0 ? ((v / base) * 100).toFixed(0) : '0');

  const cobradoPct = totalDisbursed > 0 ? (totalCobrado / totalDisbursed) * 100 : 0;
  const futurePct = totalDisbursed > 0 ? (totalFuture / totalDisbursed) * 100 : 0;
  const overduePct = totalDisbursed > 0 ? (totalOverdue / totalDisbursed) * 100 : 0;

  return (
    <div className="rounded-md border border-border bg-card p-3">
      <p className="mb-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        Funnel agregado de cartera
      </p>

      <FunnelStage
        label="Ofertas"
        sub={`${props.offerCount} ${props.offerCount === 1 ? 'recibida' : 'recibidas'}`}
        widthPct={100}
        amount={totalOffers}
        pct="100%"
      />
      <Dropoff
        amount={props.offerToAppDrop}
        text={
          props.offerToAppDrop > 0
            ? `Ofertas no convertidas en solicitud · -${formatCurrency(props.offerToAppDrop, 'MXN', 'es-MX')}`
            : 'Todas las ofertas avanzaron a solicitud · $0'
        }
      />

      <FunnelStage
        label="Solicitudes"
        sub={`${props.appCount} firmadas`}
        widthPct={parseFloat(pct(totalApps))}
        amount={totalApps}
        pct={`${pct(totalApps)}%`}
      />
      <Dropoff
        amount={props.appToCreditDrop}
        text={
          props.appToCreditDrop > 0
            ? `Solicitudes no desembolsadas · -${formatCurrency(props.appToCreditDrop, 'MXN', 'es-MX')}`
            : 'Todas las solicitudes avanzaron a desembolso · $0'
        }
      />

      <FunnelStage
        label="Desembolsos"
        sub={`${props.creditCount} ejecutados`}
        widthPct={parseFloat(pct(totalDisbursed))}
        amount={totalDisbursed}
        pct={`${relPct(totalDisbursed, totalApps)}%`}
      />
      <Dropoff text="Pagos en curso" zero />

      <div className="mb-1 grid grid-cols-[110px_1fr_90px] items-center gap-3">
        <div className="text-xs">
          <span className="font-medium text-foreground">Estado del cobro</span>
          <span className="block text-[10px] text-muted-foreground">
            de {formatCurrency(totalDisbursed, 'MXN', 'es-MX')}
          </span>
        </div>
        <div className="h-[22px] overflow-hidden rounded bg-[#0f172a]">
          <div className="flex h-full" style={{ width: `${pct(totalDisbursed)}%` }}>
            {cobradoPct > 0 && (
              <div
                className="flex h-full items-center bg-blue-500 px-2 text-[10px] font-semibold text-white"
                style={{ width: `${cobradoPct}%` }}
              >
                {cobradoPct >= 12 ? formatCurrency(totalCobrado, 'MXN', 'es-MX') : ''}
              </div>
            )}
            {futurePct > 0 && (
              <div
                className="flex h-full items-center bg-green-500 px-2 text-[10px] font-semibold text-white"
                style={{ width: `${futurePct}%` }}
              >
                {futurePct >= 12 ? formatCurrency(totalFuture, 'MXN', 'es-MX') : ''}
              </div>
            )}
            {overduePct > 0 && (
              <div
                className="flex h-full items-center bg-red-500 px-2 text-[10px] font-semibold text-white"
                style={{ width: `${overduePct}%` }}
              >
                {overduePct >= 12 ? formatCurrency(totalOverdue, 'MXN', 'es-MX') : ''}
              </div>
            )}
          </div>
        </div>
        <div className="text-right text-sm font-bold tabular-nums text-foreground">
          {cobradoPct.toFixed(0)}%
          <span className="block text-[10px] font-medium text-muted-foreground">cobrado</span>
        </div>
      </div>

      <div className="ml-[120px] flex flex-wrap gap-3 text-[10px] text-muted-foreground">
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-sm bg-blue-500" />
          Cobrado {formatCurrency(totalCobrado, 'MXN', 'es-MX')}
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="inline-block h-2 w-2 rounded-sm bg-green-500" />
          Por vencer {formatCurrency(totalFuture, 'MXN', 'es-MX')}
        </span>
        {totalOverdue > 0 && (
          <span className="inline-flex items-center gap-1">
            <span className="inline-block h-2 w-2 rounded-sm bg-red-500" />
            Vencido {formatCurrency(totalOverdue, 'MXN', 'es-MX')}
          </span>
        )}
      </div>
    </div>
  );
}

function FunnelStage({
  label, sub, widthPct, amount, pct,
}: { label: string; sub: string; widthPct: number; amount: number; pct: string }) {
  return (
    <div className="mb-2 grid grid-cols-[110px_1fr_90px] items-center gap-3">
      <div className="text-xs">
        <span className="font-medium text-foreground">{label}</span>
        <span className="block text-[10px] text-muted-foreground">{sub}</span>
      </div>
      <div className="h-[22px] overflow-hidden rounded bg-[#0f172a]">
        <div
          className="flex h-full items-center rounded bg-blue-500 px-2 text-[10px] font-semibold text-white"
          style={{ width: `${Math.max(0, Math.min(100, widthPct))}%` }}
        >
          {formatCurrency(amount, 'MXN', 'es-MX')}
        </div>
      </div>
      <div className="text-right text-sm font-bold tabular-nums text-foreground">
        {formatCurrency(amount, 'MXN', 'es-MX')}
        <span className="block text-[11px] font-medium text-muted-foreground">{pct}</span>
      </div>
    </div>
  );
}

function Dropoff({ amount = 0, text, zero }: { amount?: number; text: string; zero?: boolean }) {
  const isZero = zero || amount === 0;
  return (
    <div
      className={`-mt-0.5 mb-1.5 flex justify-end pr-[100px] text-[11px] font-medium ${isZero ? 'text-muted-foreground' : 'text-red-300'}`}
    >
      <span className="mr-1 font-bold">↓</span>
      {text}
    </div>
  );
}

// ── Filter pills ──────────────────────────────────────────────────────────

function FilterPills({
  filter,
  onFilterChange,
  counts,
}: {
  filter: 'all' | 'live' | 'problem' | 'paid';
  onFilterChange: (f: 'all' | 'live' | 'problem' | 'paid') => void;
  counts: { active: number; delinquent: number; defaulted: number; paidOff: number; total: number };
}) {
  const live = counts.active + counts.delinquent;
  const problem = counts.delinquent + counts.defaulted;
  const opts: { key: typeof filter; label: string; count: number }[] = [
    { key: 'all', label: 'Todos', count: counts.total },
    { key: 'live', label: 'Vivos', count: live },
    { key: 'problem', label: 'Problemas', count: problem },
    { key: 'paid', label: 'Liquidados', count: counts.paidOff },
  ];

  return (
    <div className="flex flex-wrap items-center gap-1">
      {opts.map((o) => (
        <button
          key={o.key}
          type="button"
          onClick={() => onFilterChange(o.key)}
          className={`rounded-md border px-2 py-0.5 text-[10px] font-medium transition-colors ${
            filter === o.key
              ? 'border-primary bg-primary/10 text-primary'
              : 'border-border bg-card text-muted-foreground hover:border-primary/50 hover:text-foreground'
          }`}
        >
          {o.label} · {o.count}
        </button>
      ))}
    </div>
  );
}

// ── Credit row ────────────────────────────────────────────────────────────

function CreditRow({ credit }: { credit: BackofficeCreditsListItem }) {
  const score = creditScore(credit);
  const color = scoreColor(score);
  const badge = statusBadge(credit.status);
  const weeksPaid = credit.weeks_paid ?? 0;
  const weeksTotal = credit.weeks_total ?? credit.term_weeks ?? 0;

  return (
    <li className="grid grid-cols-[4px_1fr_auto_auto] items-center gap-2.5 border-b border-border/30 py-2 pr-1 last:border-b-0">
      <div className={`h-7 w-1 rounded-sm ${color.bar}`} />
      <div className="flex min-w-0 flex-col gap-0.5">
        <div className="flex flex-wrap items-center gap-1.5 text-xs font-semibold text-foreground">
          <span className="truncate">{credit.full_name || '—'}</span>
          <span className={`inline-block rounded px-1.5 py-0.5 text-[9px] font-bold uppercase ${badge.className}`}>
            {badge.label}
          </span>
        </div>
        <div className="text-[11px] text-muted-foreground">
          {formatCurrency(credit.amount_mxn, 'MXN', 'es-MX')} · {credit.term_weeks} sem
          {weeksTotal > 0 && ` · ${weeksPaid}/${weeksTotal} cuotas`}
        </div>
      </div>
      <div className={`w-8 text-right text-base font-extrabold tabular-nums ${color.text}`}>
        {Math.round(score)}
      </div>
      <div className="flex flex-col items-end pr-1 text-[9px] leading-none text-muted-foreground">
        <span>{color.label.split(' ')[0] ?? ''}</span>
        <span>{color.label.split(' ').slice(1).join(' ') || ''}</span>
      </div>
    </li>
  );
}

// ── Insights de cartera ───────────────────────────────────────────────────

function buildCarteraInsights({
  offers,
  signedApps,
  credits,
  counts,
  totalOverdue,
  healthGlobal,
}: {
  offers: { max_amount_mxn: number }[];
  signedApps: unknown[];
  credits: BackofficeCreditsListItem[];
  counts: { active: number; delinquent: number; defaulted: number; paidOff: number; total: number };
  totalOverdue: number;
  healthGlobal: number;
}): string[] {
  const insights: string[] = [];

  if (offers.length > 0 && signedApps.length > 0) {
    const conv = (signedApps.length / offers.length) * 100;
    const sentClass = conv >= 50 ? 'text-green-400' : 'text-red-300';
    insights.push(
      `Tasa de conversión global: <span class="${sentClass}"><b>${conv.toFixed(0)}%</b></span> (${signedApps.length} de ${offers.length} ofertas firmadas).`,
    );
  }

  if (credits.length > 0) {
    const liveCount = counts.active + counts.delinquent;
    if (liveCount > 0) {
      const onTimePct = (counts.active / liveCount) * 100;
      insights.push(
        `<b>${counts.active}</b> de <b>${liveCount}</b> créditos vivos están al día (<span class="text-green-400">${onTimePct.toFixed(0)}%</span>).`,
      );
    }
  }

  if (counts.delinquent > 0 || counts.defaulted > 0) {
    const problem = counts.delinquent + counts.defaulted;
    insights.push(
      `<b><span class="text-red-300">${problem}</span> ${problem === 1 ? 'crédito requiere' : 'créditos requieren'} atención</b>: ${counts.delinquent} delincuentes, ${counts.defaulted} en default.`,
    );
  }

  if (totalOverdue > 0) {
    insights.push(
      `Monto vencido sin cobrar en cartera: <span class="text-red-300"><b>${formatCurrency(totalOverdue, 'MXN', 'es-MX')}</b></span>.`,
    );
  }

  if (counts.paidOff > 0) {
    insights.push(
      `<b>${counts.paidOff}</b> créditos liquidados a la fecha — <span class="text-green-400">historial positivo</span>.`,
    );
  }

  if (healthGlobal > 0) {
    const sent = healthGlobal >= 70 ? 'text-green-400' : healthGlobal >= 40 ? 'text-yellow-400' : 'text-red-300';
    const word = healthGlobal >= 70 ? 'sólida' : healthGlobal >= 40 ? 'aceptable' : 'deteriorada';
    insights.push(
      `Salud crediticia de cartera: <span class="${sent}"><b>${Math.round(healthGlobal)}/100</b> · ${word}</span>.`,
    );
  }

  return insights;
}
